import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import { IS_TEST_ENVIRONMENT } from '../utils/environment';
import type { Impersonation } from '../features/auth/impersonation';

/** The tab hanging from the band. */
const TAB_SX: SxProps<Theme> = {
    px: 1.5,
    py: 0.25,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4
};

/**
 * A band along the top edge with a tab in the middle, above everything —
 * fullscreen dialogs and the login page too. It marks the two states nobody may
 * forget they are in:
 *
 * - the **test installation**, which holds a copy of the real data and must
 *   never be mistaken for it;
 * - an administrator **signed in as somebody else** ("Anmelden als"), with the
 *   way back.
 *
 * The band itself takes no clicks, so nothing beneath it is out of reach; only
 * the way back does.
 */
export default function TopEdgeBanner({
    impersonation,
    onEndImpersonation
}: {
    impersonation: Impersonation | null;
    onEndImpersonation: () => void;
}) {
    const [leaving, setLeaving] = useState(false);
    if (!IS_TEST_ENVIRONMENT && !impersonation) {
        return null;
    }
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: (theme) => theme.zIndex.tooltip + 1,
                pointerEvents: 'none',
                display: 'flex',
                justifyContent: 'center',
                gap: 0.5,
                borderTop: 4,
                borderColor: 'warning.main'
            }}
        >
            {IS_TEST_ENVIRONMENT && (
                <Box
                    aria-hidden
                    sx={{
                        ...TAB_SX,
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        letterSpacing: 1,
                        textTransform: 'uppercase'
                    }}
                >
                    <FormattedMessage id="app.testEnvironment" />
                </Box>
            )}
            {impersonation && (
                <Box
                    role="status"
                    sx={{
                        ...TAB_SX,
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <FormattedMessage
                            id="auth.impersonate.banner"
                            values={{ name: impersonation.targetName }}
                        />
                    </Box>
                    <ButtonBase
                        disabled={leaving}
                        onClick={() => {
                            setLeaving(true);
                            onEndImpersonation();
                        }}
                        sx={{
                            pointerEvents: 'auto',
                            flexShrink: 0,
                            px: 0.75,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'currentColor',
                            font: 'inherit',
                            whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                        }}
                    >
                        <Box
                            component="span"
                            sx={{ display: { xs: 'none', sm: 'inline' } }}
                        >
                            <FormattedMessage id="auth.impersonate.end" />
                        </Box>
                        <Box
                            component="span"
                            sx={{ display: { xs: 'inline', sm: 'none' } }}
                        >
                            <FormattedMessage id="auth.impersonate.endShort" />
                        </Box>
                    </ButtonBase>
                </Box>
            )}
        </Box>
    );
}
